/**
 * Centralized helper functions to avoid code duplication across components
 */

// ============================================
// DATE FORMATTING HELPERS
// ============================================

/**
 * Converts various date formats (Firestore Timestamp, Date, number, string) to Date object
 * @param timestamp - The date value to convert
 * @returns Date object
 */
export const toDate = (timestamp: any): Date => {
  if (!timestamp) return new Date();

  // Firestore Timestamp
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }

  // Already a Date object
  if (timestamp instanceof Date) {
    return timestamp;
  }

  // Unix timestamp (number)
  if (typeof timestamp === 'number') {
    return new Date(timestamp);
  }

  // String or other - try to parse
  return new Date(timestamp);
};

/**
 * Formats a date value to a localized string (en-IN format)
 * @param timestamp - The date value to format
 * @returns Formatted date string (e.g., "25/12/2024")
 */
export const formatDateString = (timestamp: any): string => {
  if (!timestamp) return '-';
  const date = toDate(timestamp);
  return date.toLocaleDateString('en-IN');
};

/**
 * Formats a date value using date-fns format
 * @param timestamp - The date value to format
 * @param formatStr - Format string (default: 'MMM dd, yyyy')
 * @returns Formatted date string
 */
export const formatDateWithPattern = (timestamp: any, formatStr: string = 'MMM dd, yyyy'): string => {
  if (!timestamp) return '-';
  const date = toDate(timestamp);
  // Using toLocaleDateString with options for flexibility
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  };
  return date.toLocaleDateString('en-US', options);
};

// ============================================
// CURRENCY FORMATTING HELPERS
// ============================================

/**
 * Formats a number as Indian Rupees currency
 * @param amount - The amount to format
 * @returns Formatted currency string (e.g., "₹1,234.00")
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(amount);
};

/**
 * Formats a number with Indian locale (with commas)
 * @param num - The number to format
 * @returns Formatted number string (e.g., "1,23,456")
 */
export const formatNumber = (num: number): string => {
  return num.toLocaleString('en-IN');
};

// ============================================
// STATUS HELPERS
// ============================================

/**
 * Returns Tailwind CSS classes for order status badges
 * @param status - The status name
 * @returns CSS class string
 */
export const getOrderStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'seedingpending':
      return 'text-orange-800 bg-orange-200 border border-orange-300';
    case 'pending':
      return 'text-amber-800 bg-amber-200 border border-amber-300';
    case 'in_progress':
    case 'in progress':
      return 'text-blue-800 bg-blue-200 border border-blue-300';
    case 'ready':
      return 'text-purple-800 bg-purple-200 border border-purple-300';
    case 'delivered':
      return 'text-green-800 bg-green-200 border border-green-300';
    case 'complete':
      return 'text-green-800 bg-green-200 border border-green-300';
    case 'cancelled':
      return 'text-red-800 bg-red-200 border border-red-300';
    case 'deleted':
      return 'text-gray-600 bg-gray-300 border border-gray-400';
    default:
      return 'text-gray-800 bg-gray-200 border border-gray-300';
  }
};

/**
 * Returns Tailwind CSS classes for order status badges (with dark mode support)
 * @param status - The status name
 * @returns CSS class string
 */
export const getOrderStatusColorWithDarkMode = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'seedingpending':
      return 'text-orange-800 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-300 border border-orange-200 dark:border-orange-700';
    case 'pending':
      return 'text-yellow-800 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-700';
    case 'in_progress':
    case 'in progress':
      return 'text-blue-800 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-700';
    case 'ready':
      return 'text-green-800 bg-green-100 dark:bg-green-900/30 dark:text-green-300 border border-green-200 dark:border-green-700';
    case 'delivered':
      return 'text-gray-800 bg-gray-100 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600';
    case 'complete':
      return 'text-green-800 bg-green-100 dark:bg-green-900/30 dark:text-green-300 border border-green-200 dark:border-green-700';
    case 'cancelled':
      return 'text-red-800 bg-red-100 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-700';
    case 'deleted':
      return 'text-gray-600 bg-gray-200 dark:bg-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-500';
    default:
      return 'text-gray-800 bg-gray-100 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600';
  }
};

/**
 * Returns the dot color class for status indicators
 * @param status - The status name
 * @returns CSS class string for the dot
 */
export const getStatusDotColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'seedingpending':
      return 'bg-orange-600';
    case 'pending':
      return 'bg-yellow-600';
    case 'in_progress':
    case 'in progress':
      return 'bg-blue-600';
    case 'ready':
      return 'bg-green-600';
    case 'delivered':
      return 'bg-gray-600';
    case 'complete':
      return 'bg-green-600';
    case 'cancelled':
      return 'bg-red-600';
    case 'deleted':
      return 'bg-gray-500';
    default:
      return 'bg-gray-600';
  }
};

// ============================================
// VALIDATION HELPERS
// ============================================

/**
 * Validates Indian phone number format
 * @param phone - Phone number string
 * @returns Error message or null if valid
 */
export const validatePhoneNumber = (phone: string): string | null => {
  const cleanPhone = phone.replace(/\D/g, '');
  if (cleanPhone.length !== 10) {
    return 'Phone number must be 10 digits';
  }
  if (!/^[6-9]/.test(cleanPhone)) {
    return 'Phone number must start with 6, 7, 8, or 9';
  }
  return null;
};

/**
 * Validates that a value is a positive number
 * @param value - Value to validate
 * @param fieldName - Field name for error message
 * @returns Error message or null if valid
 */
export const validatePositiveNumber = (value: string | number, fieldName: string): string | null => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num) || num < 0) {
    return `${fieldName} must be a valid positive number`;
  }
  return null;
};

// ============================================
// MISCELLANEOUS HELPERS
// ============================================

/**
 * Generates initials from a name (e.g., "John Doe" -> "JD")
 * @param name - Full name
 * @param maxChars - Maximum characters to return (default: 2)
 * @returns Initials string
 */
export const getInitials = (name: string, maxChars: number = 2): string => {
  if (!name) return '';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, maxChars);
};

/**
 * Truncates a string to a maximum length with ellipsis
 * @param str - String to truncate
 * @param maxLength - Maximum length (default: 50)
 * @returns Truncated string
 */
export const truncateString = (str: string, maxLength: number = 50): string => {
  if (!str || str.length <= maxLength) return str;
  return str.slice(0, maxLength) + '...';
};

/**
 * Debounce function for search inputs
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};
